import { CheckCircleFilled } from '@ant-design/icons';
import { Button, Spin } from 'antd';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { isWechatBrowser } from '../../../components/PayQRCodes';
import { replaceTo } from '../../../components/PayQRCodes/navigation';
import { notifyOrderPaid, orderKind, queryPaid } from '../../../components/PayQRCodes/usePayOrder';
import styles from './index.module.scss';

// 微信里一直查单：用户去浏览器付完回到微信，这里直接显示结果（每 2 秒一次，最多 10 分钟）
const POLL_INTERVAL = 2000;
const POLL_MAX = 300;

const WRITING_KINDS = ['writing', 'chapter', 'upgrade'];

/** 只放行支付宝的付款链接，避免这个页面被拿去跳任意网址 */
export const alipayPayUrl = (raw) => {
    try {
        const url = new URL(raw);
        return url.protocol === 'https:' && /(^|\.)alipay(dev)?\.com$/i.test(url.hostname) ? url.href : '';
    } catch {
        return '';
    }
};

/** 手机网站支付链接（GET）的 biz_content 里带着商户订单号 */
export const orderNoOf = (payUrl) => {
    try {
        const biz = new URL(payUrl).searchParams.get('biz_content');
        return JSON.parse(biz)?.out_trade_no || '';
    } catch {
        return '';
    }
};

/**
 * 付完后去哪：优先付款链接里的 return_url（必须是本站地址，只取路径），
 * 没有的话写作订单去订单结果页，其余回上一页。
 */
export const continuePathOf = (payUrl, orderNo, origin = typeof window === 'undefined' ? '' : window.location.origin) => {
    try {
        const ret = new URL(payUrl).searchParams.get('return_url');
        if (ret) {
            const url = new URL(ret, origin || undefined);
            if (url.origin === origin) return `${url.pathname}${url.search}`;
        }
    } catch {
        // 没有或不是合法地址
    }
    if (orderNo && WRITING_KINDS.includes(orderKind(orderNo))) return `/order/result?no=${encodeURIComponent(orderNo)}`;
    return '';
};

/**
 * 支付宝付款中转页（PayQRCodes 的 alipayGuideHref）：/alipayGo?url=付款链接&orderNo=
 * 微信内置浏览器屏蔽支付宝，这里引导用户点右上角「在浏览器打开」，同时查单，付完回到微信直接显示结果；
 * 在系统浏览器里打开时立即跳到支付宝付款链接，拉起支付宝 App。
 */
export default function AlipayGo() {
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const payUrl = alipayPayUrl(params.get('url') || '');
    const inWechat = isWechatBrowser();
    const orderNo = params.get('orderNo') || orderNoOf(payUrl);
    const [paid, setPaid] = useState(false);
    const [checking, setChecking] = useState(false);
    const stateRef = useRef({ attempt: 0, timer: null, done: false });

    useEffect(() => {
        const prev = document.title;
        document.title = '支付宝付款 - 灵感风暴';
        return () => {
            document.title = prev;
        };
    }, []);

    // 系统浏览器：直接跳支付宝
    useEffect(() => {
        if (payUrl && !inWechat) replaceTo(payUrl);
    }, [payUrl, inWechat]);

    // manual：用户点「我已付款」时才让按钮转圈，后台轮询不影响按钮
    const check = useCallback(
        async (manual = false) => {
            if (!orderNo || stateRef.current.done) return false;
            if (manual) setChecking(true);
            try {
                if (await queryPaid(orderNo)) {
                    stateRef.current.done = true;
                    if (stateRef.current.timer) clearTimeout(stateRef.current.timer);
                    notifyOrderPaid(orderNo);
                    setPaid(true);
                    return true;
                }
            } catch {
                // 网络抖动，下次再查
            } finally {
                if (manual) setChecking(false);
            }
            return false;
        },
        [orderNo],
    );

    // 微信里轮询到账；页面重新可见（从浏览器切回微信）时立刻查一次
    useEffect(() => {
        if (!inWechat || !orderNo) return undefined;
        const state = stateRef.current;
        const tick = async () => {
            if (state.done || state.attempt >= POLL_MAX) return;
            state.attempt += 1;
            if (await check()) return;
            state.timer = setTimeout(tick, POLL_INTERVAL);
        };
        tick();
        const onVisible = () => {
            if (document.visibilityState === 'visible' && !state.done) check();
        };
        document.addEventListener('visibilitychange', onVisible);
        return () => {
            if (state.timer) clearTimeout(state.timer);
            document.removeEventListener('visibilitychange', onVisible);
        };
    }, [inWechat, orderNo, check]);

    // 从原页面整页跳过来的，后退就回到原页面；直接打开的没有上一页，回首页
    const goBack = () => (window.history.length > 1 ? window.history.back() : navigate('/'));

    const goOn = () => {
        const path = continuePathOf(payUrl, orderNo);
        if (path) navigate(path, { replace: true });
        else goBack();
    };

    if (!payUrl) {
        return (
            <div className={styles.page}>
                <div className={styles.center}>
                    <h2 className={styles.title}>付款链接无效</h2>
                    <p className={styles.note}>请回到原页面重新发起支付</p>
                    <Button size="large" onClick={goBack}>
                        返回
                    </Button>
                </div>
            </div>
        );
    }

    if (!inWechat) {
        return (
            <div className={styles.page}>
                <div className={styles.center}>
                    <Spin size="large" />
                    <p className={styles.note}>正在打开支付宝…</p>
                    <a href={payUrl}>没有自动跳转？点这里打开</a>
                </div>
            </div>
        );
    }

    if (paid) {
        return (
            <div className={styles.page}>
                <div className={styles.center}>
                    <CheckCircleFilled className={styles.paidIcon} />
                    <h2 className={styles.title}>付款成功</h2>
                    {orderNo ? <p className={styles.note}>订单号 {orderNo}</p> : null}
                    <Button type="primary" size="large" onClick={goOn}>
                        继续
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.page}>
            <svg className={styles.arrow} viewBox="0 0 80 80" aria-hidden="true">
                <path d="M12 72 C 30 44, 44 28, 66 14" />
                <path d="M48 10 L68 12 L62 31" />
            </svg>
            <div className={styles.guide}>
                <h2 className={styles.title}>请在浏览器中打开付款</h2>
                <ol className={styles.steps}>
                    <li>
                        点右上角 <b>···</b>
                    </li>
                    <li>
                        选择「<b>在浏览器打开</b>」
                    </li>
                    <li>在浏览器里打开支付宝完成付款，付完回到微信</li>
                </ol>
                <p className={styles.note}>
                    微信不支持直接打开支付宝
                    {orderNo ? '，付款后这里会自动显示结果' : ''}
                </p>
                <div className={styles.actions}>
                    {orderNo ? (
                        <Button size="large" loading={checking} onClick={() => check(true)}>
                            我已付款
                        </Button>
                    ) : null}
                    <Button size="large" onClick={goBack}>
                        返回
                    </Button>
                </div>
            </div>
        </div>
    );
}
